import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDeviationsAllTime, HercaRecord } from '../api';
import Spinner from '../components/common/Spinner';
import './Dashboard.css';

interface Stats {
  total: number;
  byArea: Record<string, number>;
  byEmployee: Record<string, string[]>;
  recent: HercaRecord[];
}

const Dashboard = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getDeviationsAllTime()
      .then((data) => {
        setStats({
          total: data.totalDeviations,
          byArea: data.deviationsByArea ?? {},
          byEmployee: data.deviationsByEmployee ?? {},
          recent: (data.deviations ?? []).slice(-5).reverse(),
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const topArea = stats
    ? Object.entries(stats.byArea).sort((a, b) => b[1] - a[1])[0]
    : null;

  const topEmployee = stats
    ? Object.entries(stats.byEmployee).sort((a, b) => b[1].length - a[1].length)[0]
    : null;

  return (
    <div>
      <h1 className="page-title">Dashboard</h1>
      <p className="page-subtitle">Overview of all deviation records</p>

      {loading && <Spinner />}
      {error && <p className="error-banner">{error}</p>}

      {stats && (
        <>
          <div className="stat-cards">
            <div className="stat-card stat-card--primary">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Herca Records</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{Object.keys(stats.byArea).length}</span>
              <span className="stat-label">Areas Affected</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{Object.keys(stats.byEmployee).length}</span>
              <span className="stat-label">Employees Involved</span>
            </div>
            {topArea && (
              <div className="stat-card">
                <span className="stat-value">{topArea[1]}</span>
                <span className="stat-label">Top Area: {topArea[0]}</span>
              </div>
            )}
          </div>

          <div className="dash-grid">
            <div className="dash-card">
              <h3 className="dash-card-title">Deviations by Area</h3>
              {Object.keys(stats.byArea).length === 0 ? (
                <p className="empty-text">No data yet</p>
              ) : (
                <div className="bar-list">
                  {Object.entries(stats.byArea)
                    .sort((a, b) => b[1] - a[1])
                    .map(([area, count]) => (
                      <div key={area} className="bar-row">
                        <span className="bar-label">{area}</span>
                        <div className="bar-track">
                          <div
                            className="bar-fill"
                            style={{ width: `${(count / stats.total) * 100}%` }}
                          />
                        </div>
                        <span className="bar-count">{count}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="dash-card">
              <h3 className="dash-card-title">Top Employees by Deviations</h3>
              {Object.keys(stats.byEmployee).length === 0 ? (
                <p className="empty-text">No data yet</p>
              ) : (
                <div className="bar-list">
                  {Object.entries(stats.byEmployee)
                    .sort((a, b) => b[1].length - a[1].length)
                    .slice(0, 8)
                    .map(([emp, areas]) => (
                      <div key={emp} className="bar-row">
                        <span className="bar-label">{emp}</span>
                        <div className="bar-track">
                          <div
                            className="bar-fill bar-fill--emp"
                            style={{ width: `${(areas.length / stats.total) * 100}%` }}
                          />
                        </div>
                        <span className="bar-count">{areas.length}</span>
                      </div>
                    ))}
                </div>
              )}
              {topEmployee && (
                <p className="top-note">
                  Most deviations: <strong>{topEmployee[0]}</strong> ({topEmployee[1].length})
                </p>
              )}
            </div>
          </div>

          <div className="dash-card dash-card--full">
            <div className="dash-card-header">
              <h3 className="dash-card-title">Recent Records</h3>
              <button className="link-btn" onClick={() => navigate('/herca')}>
                View all →
              </button>
            </div>
            {stats.recent.length === 0 ? (
              <p className="empty-text">No records yet</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>DR Code</th>
                    <th>Employee</th>
                    <th>Area</th>
                    <th>Deviation</th>
                    <th>Incident Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent.map((r) => (
                    <tr key={r._id}>
                      <td>{r.deviationRecordCode}</td>
                      <td>{r.employeeId?.name}</td>
                      <td>{r.areaId?.name}</td>
                      <td>{r.deviationGapId?.name}</td>
                      <td>{new Date(r.incidentDate).toLocaleDateString('en-GB')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
